import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { usePublicProfile, useTrackPublicProfile } from "@seabrookstudios/pitch2table";
import { useParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { PublicSiteFrame } from "./PublicSiteFrame";
import { EmptyDetailCardWithLogin } from "../core/EmptyDetailCard";
import { formatContactLocation, formatFullName } from "@seabrookstudios/pitch2table-core";
import { UserAvatarCardCover } from "../users/UserAvatar";
import { ClickToRevealEmail } from "./ClickToReveal";
import CardContent from "@mui/material/CardContent";
import { Now } from "../core/Now";
import { formatTimeZone } from "../core/date-formatting";
import { useInvokeOnceWhenNotOwn } from "./useIsDesigner";

const PrivateProfile = () => {
  return (
    <PublicSiteFrame>
      <EmptyDetailCardWithLogin text="This designer profile is private (or the account does not or no longer exists). Please contact the designer directly." />
    </PublicSiteFrame>
  );
};

/**
 * @param {object} props
 * @param {string} props.label
 * @param {import("react").ReactNode} props.children

 */
const ProfileRow = ({ label, children }) => {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} flex={1} display="flex" sx={{ mb: { xs: 1, sm: 0 } }}>
      <Box flex={{ xs: "none", sm: 1 }} display="flex" alignItems="center" sx={{ mb: { xs: 0.5, sm: 0 } }}>
        <Typography fontWeight="bold" variant="body2" align="left">
          {label}
        </Typography>
      </Box>
      <Box flex={{ xs: "none", sm: 1 }}>
        <Typography>{children}</Typography>
      </Box>
    </Stack>
  );
};

/**
 * @param {object} props
 * @param {object} props.username
 */
const PublicDesigner = ({ username }) => {
  const { data: profile, isLoading } = usePublicProfile(username);
  const trackPublicProfile = useTrackPublicProfile(username);
  useInvokeOnceWhenNotOwn(trackPublicProfile, username);

  if (!profile || isLoading) {
    return <CircularProgress />;
  }

  if (!profile.isPublic) {
    return <PrivateProfile />;
  }

  return (
    <PublicSiteFrame>
      <Card>
        <Stack direction={{ xs: "column", sm: "row" }} flex={1} display="flex">
          <Box
            flex={1}
            justifyContent="center"
            display="flex"
            sx={{
              alignItems: "center",
            }}
          >
            <UserAvatarCardCover contact={profile} editable={false} />
          </Box>
          <Box flex={1} display="flex">
            <CardContent style={{ flex: 1 }}>
              <Stack direction="column" flex={1} spacing={1}>
                <Box paddingBottom={1}>
                  <Typography variant="h4">{formatFullName(profile)}</Typography>
                </Box>
                <Stack direction="column" spacing={1} flex={1}>
                  <ProfileRow label="Preferred Name">{profile.preferredName}</ProfileRow>
                  <ProfileRow label="Pronouns">{profile.pronouns}</ProfileRow>
                  {profile.location && (
                    <ProfileRow label="Location">{formatContactLocation(profile.location)}</ProfileRow>
                  )}
                  {profile.location?.timezone && (
                    <ProfileRow label="Time Zone">{formatTimeZone(profile.location.timezone)}</ProfileRow>
                  )}
                  {profile.location?.timezone && (
                    <ProfileRow label="Current Time">
                      <Now timezone={profile.location.timezone} />
                    </ProfileRow>
                  )}
                  {profile.showEmail && (
                    <ProfileRow label="Email">
                      <ClickToRevealEmail username={username} />
                    </ProfileRow>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Box>
        </Stack>
      </Card>
    </PublicSiteFrame>
  );
};

export const PublicDesignerFromRoute = () => {
  const username = useParams().username;

  return <PublicDesigner username={username} />;
};
